var _excluded = ["label", "max", "value", "variant"];

var _templateObject, _templateObject2, _templateObject3;

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _taggedTemplateLiteralLoose(strings, raw) { if (!raw) { raw = strings.slice(0); } strings.raw = raw; return strings; }

import React from 'react';
import styled from 'styled-components';
import { string, number, shape, oneOfType, oneOf, bool } from 'prop-types';
import { backgroundColor, borders, elevation, positions, display, spacing as systemSpacing } from '@gympass/yoga-system';
import { charLength } from '../../shared';
var ProgressBar = styled.progress(_templateObject || (_templateObject = _taggedTemplateLiteralLoose(["\n  width: 100%;\n\n  border: none;\n\n  ", "\n"])), function (_ref) {
  var variant = _ref.variant,
      _ref$theme$yoga = _ref.theme.yoga,
      color = _ref$theme$yoga.colors[variant],
      progress = _ref$theme$yoga.components.progress;
  return "\n  height: " + progress.height + "px;\n\n  &&,\n  &::-webkit-progress-value,\n  &[value]::-webkit-progress-bar,\n  &:not(value)::-webkit-progress-bar {\n    border-radius: " + progress.border.radius + "px;\n  }\n\n  &:not(value)::-moz-progress-bar,\n  &[value]::-moz-progress-bar {\n    border-radius: " + progress.border.radius + "px;\n  }\n\n  &:not(value)::-webkit-progress-bar {\n    background-color: " + progress.backgroundColor.bar + ";\n  }\n\n  &::-webkit-progress-value {\n    background-color: " + color + ";\n  }\n\n  &&,\n  &:not(value)::-moz-progress-bar {\n    background-color: " + progress.backgroundColor.bar + ";\n  }\n\n  &[value]::-moz-progress-bar {\n    background-color: " + color + ";\n  }\n";
});
var Label = styled.label(_templateObject2 || (_templateObject2 = _taggedTemplateLiteralLoose(["\n  ", "\n"])), function (_ref2) {
  var _ref2$theme$yoga = _ref2.theme.yoga,
      progress = _ref2$theme$yoga.components.progress,
      text = _ref2$theme$yoga.colors.text;
  return "\n  color: " + text.secondary + ";\n  font-size: " + progress.label.font.size + "px;\n  letter-spacing: normal;\n";
});
var Wrapper = styled.div(_templateObject3 || (_templateObject3 = _taggedTemplateLiteralLoose(["\n  display: flex;\n\n  width: 100%;\n\n  ", "\n\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n"])), function (_ref3) {
  var isNumber = _ref3.isNumber,
      align = _ref3.align,
      spacing = _ref3.theme.yoga.spacing;
  return "\n    " + (isNumber ? "\n        " + Label + " {\n          width: 22px;\n\n          margin-" + (align === 'right' ? 'left' : 'right') + ": " + spacing.xxsmall + "px;\n        }\n\n        " + ProgressBar + " {\n          margin: auto;\n        }\n    " : "\n        flex-direction: column;\n\n        " + Label + " {\n          max-width: 280px;\n\n          margin-top: " + spacing.xxsmall + "px;\n        }\n      ") + "\n\n      " + (align === 'right' ? 'align-items: flex-end; text-align: right;' : 'align-items: flex-start;') + "\n      " + (isNumber && align === 'left' ? 'flex-direction: row-reverse;' : '') + "\n  ";
}, backgroundColor, borders, elevation, positions, display, systemSpacing);
Wrapper.propTypes = {
  align: oneOf(['left', 'right']),
  isNumber: bool
};
Wrapper.defaultProps = {
  align: 'left',
  isNumber: false
};
/** The Progress is a component used to indicate a progress of an indicator
 * of quantity. The use of labels numeric or alphabetic can increase the user
 * understanding. */

var Progress = /*#__PURE__*/React.forwardRef(function (_ref4, ref) {
  var label = _ref4.label,
      max = _ref4.max,
      value = _ref4.value,
      variant = _ref4.variant,
      props = _objectWithoutPropertiesLoose(_ref4, _excluded);

  var isNumber = !/[a-zA-Z]/g.test(label.value);
  return /*#__PURE__*/React.createElement(Wrapper, _extends({
    isNumber: isNumber,
    align: label.placement,
    ref: ref
  }, props), /*#__PURE__*/React.createElement(ProgressBar, {
    max: max,
    value: value,
    variant: variant
  }), Object.keys(label).length > 0 && (isNumber || label.value) && /*#__PURE__*/React.createElement(Label, null, label.value));
});
Progress.propTypes = {
  /** Use labels to increase users understanding. If the value is numeric, make
   * sure it has a maximum of 3 characters */
  label: shape({
    value: charLength(3, oneOfType([number, string])),
    placement: oneOf(['left', 'right'])
  }),

  /** This attribute describes how much work the task indicated by the progress
   * element requires. The max attribute, if present, must have a value greater
   * than zero and be a valid floating point number. The default value is 1. */
  max: number,

  /** This attribute specifies how much of the task that has been completed. It
   * must be a valid floating point number between 0 and max, or between 0 and 1
   * if max is omitted. If there is no value attribute, the progress bar is
   * indeterminate; this indicates that an activity is ongoing with no
   * indication of how long it is expected to take. */
  value: number,

  /** style the card following the theme (primary, secondary, vibin, hope,
   * energy, relax, peace, verve, uplift, deepPurple, deep, stamina, dark,
   * medium, light, clear, white) */
  variant: oneOf(['primary', 'secondary', 'vibin', 'hope', 'energy', 'relax', 'peace', 'verve', 'uplift', 'deepPurple', 'stamina', 'dark', 'medium', 'deep', 'light', 'clear', 'white'])
};
Progress.defaultProps = {
  label: {
    value: undefined,
    placement: 'left'
  },
  max: 1,
  value: undefined,
  variant: 'verve'
};
export default Progress;